import NextImage, { ImageLoaderProps } from 'next/image';

import { CardButton } from '../../../components/Cards/CardButton';
import { AppStoreButtons } from '../../../components/Clickables/Buttons/AppStoreButtons/AppStoreButtons';
import { Grid } from '../../../components/Grid/Grid';
import { Heading } from '../../../components/Heading/Heading';
import { getCatchCardEventProperties } from '../../../components/PostsViewer/helpers/getCatchCardEventProperties';
import { openPostViewerEventSources } from '../../../components/PostsViewer/types/openPostViewerEventSources';
import { useAnalytics } from '../../../contexts/AnalyticsContext/AnalyticsContext';
import { GetSingleSpecies_entry_catchesWithImages_edges_node as ISCatch } from '../../../interfaces/graphql';
import { getNodesFromConnection } from '../../../helpers/graphql';
import { getImgProxyLoader } from '../../../helpers/imageLoaders/imgProxy';

interface IProps {
  catchList: ISCatch[];
  displayName: string;
  openPostViewer: (catchId: string, eventSource?: openPostViewerEventSources) => Promise<void>;
}

export const RecentCatchesSection = ({ catchList, displayName, openPostViewer }: IProps) => {
  const { currentPage } = useAnalytics();

  return (
    <div
      className="col-span-4 sm:col-span-12"
      id="catches-section"
      data-testid="RecentCatchesSection"
    >
      <Heading variant="md" className="mb-10 text-center">
        Recently caught {displayName}
      </Heading>

      <Grid>
        {catchList.map(({ post }) => {
          const image = getNodesFromConnection(post.images)[0];
          const imageUrl = image.url || '';
          const imageWidth = image.width || 0;
          const imageHeight = image.height;

          const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
            getImgProxyLoader(src, width, imageWidth, imageHeight, { quality });

          return (
            <CardButton
              className="relative col-span-2 pb-[100%] hover:shadow-md sm:col-span-3"
              id={post._id}
              key={post._id}
              eventProperties={getCatchCardEventProperties({
                postId: post._id,
                currentPage,
                source: 'species-recent-catches',
              })}
              onClick={() =>
                void openPostViewer(post._id, openPostViewerEventSources.SpeciesRecentCatchImage)
              }
              ariaLabel="a recent catch of this species"
            >
              <NextImage
                alt="recently logged catches"
                className="size-full object-cover"
                src={imageUrl}
                loader={imgProxyLoader}
                fill
                sizes="(min-width: 1680px) 385px,(min-width: 600px) 25vw, 50vw"
              />
            </CardButton>
          );
        })}
      </Grid>

      <Heading variant="sm" className="mt-10 mb-6 text-center">
        Download the Fishbrain app to log your catch
      </Heading>
      <AppStoreButtons className="justify-center" source="RecentCatchesSection" />
    </div>
  );
};
